import { propertiesForTypes, recipeFor } from "./catalog";
import { nativeEntryFieldNames } from "./nativeEntryFields";
import { vocabulary } from "./vocabulary";

/** Um campo alimentando uma propriedade do catálogo. */
export interface StructuredDataFieldMapping {
  /**
   * Nome do campo, não o id. Composto (`modelo.name`) quando o campo vem de um
   * tipo relacionado — a mesma forma que `fields-with-relations` já devolve.
   */
  field: string;
  /** Caminho da propriedade — a chave de `recipes`. */
  property: string;
  /**
   * Id do `ContentTypeRelation` de onde o campo vem. Presente **apenas** em
   * campo de tipo relacionado, e é o que faz a relação ser requisitada junto com
   * o registro, sem depender de um bloco tê-la pedido.
   */
  relation?: string;
  /**
   * Correspondência de valores para propriedade de valor fechado: chave é o
   * valor do CMS em minúsculas, valor é a chave da receita.
   */
  valueMap?: Record<string, string>;
}

/** O que fica salvo em `ContentType.structuredData`. */
export interface StructuredDataConfig {
  /**
   * Entidade selecionada. Array por compatibilidade com o formato já gravado,
   * mas o admin escolhe uma só — e `validateConfig` recusa mais que uma.
   */
  types: string[];
  mappings: StructuredDataFieldMapping[];
}

export interface ConfigProblem {
  code:
    | "unknown-type"
    | "nested-only-type"
    | "multiple-types"
    | "unknown-property"
    | "property-not-in-types"
    | "unknown-field"
    | "duplicate-mapping"
    | "missing-relation"
    | "relation-field-mismatch";
  detail: string;
}

/**
 * Relações que a configuração precisa para se resolver. É o que o cms-api soma
 * ao que os blocos já pediram, e o que faz um campo de tipo relacionado chegar
 * ao registro por conta da marcação, não por conta da página.
 */
export function structuredDataRelationIds(
  config: StructuredDataConfig | null | undefined,
): string[] {
  const ids = new Set<string>();

  (config?.mappings ?? []).forEach(({ relation }) => {
    if (relation) ids.add(relation);
  });

  return Array.from(ids);
}

/**
 * Valida a configuração contra o catálogo e contra os campos que o content type
 * realmente tem. Usada pelo cms-api antes de persistir e pelo admin antes de
 * salvar, para que as duas pontas recusem exatamente as mesmas coisas.
 *
 * `relationByField` mapeia nome de campo → id da relação de onde ele vem, e
 * cobre só os campos de tipo relacionado. Campo próprio não aparece nele.
 */
export function validateConfig({
  config,
  fieldNames,
  relationByField = {},
}: {
  config: StructuredDataConfig;
  fieldNames: readonly string[];
  relationByField?: Readonly<Record<string, string>>;
}): ConfigProblem[] {
  const problems: ConfigProblem[] = [];

  config.types.forEach((type) => {
    const entry = vocabulary[type];

    if (!entry) {
      problems.push({ code: "unknown-type", detail: type });
      return;
    }

    if (entry.nestedOnly) {
      problems.push({ code: "nested-only-type", detail: type });
    }
  });

  if (config.types.length > 1) {
    problems.push({ code: "multiple-types", detail: config.types.join(", ") });
  }

  const available = new Set(
    propertiesForTypes(config.types).map((property) => property.path),
  );
  const fields = new Set([...fieldNames, ...nativeEntryFieldNames]);
  const seen = new Set<string>();

  config.mappings.forEach(({ field, property, relation }) => {
    const key = `${relation ?? ""}:${field}→${property}`;

    if (seen.has(key)) {
      problems.push({ code: "duplicate-mapping", detail: key });
    }
    seen.add(key);

    if (!fields.has(field)) {
      problems.push({ code: "unknown-field", detail: field });
    }

    const expected = relationByField[field];

    if (expected !== undefined && relation === undefined) {
      problems.push({ code: "missing-relation", detail: field });
    } else if (relation !== undefined && relation !== expected) {
      problems.push({
        code: "relation-field-mismatch",
        detail: `${field}→${relation}`,
      });
    }

    if (!available.has(property)) {
      problems.push({
        code: recipeFor(property) ? "property-not-in-types" : "unknown-property",
        detail: property,
      });
    }
  });

  return problems;
}
